import { createFileRoute, redirect } from '@tanstack/react-router';
import z from 'zod';

import { conversationDetailsQueryOptions } from '@/api/endpoints/conversations';
import { TLResourceType } from '@/api/gen/tessera-backend-api.schemas';
import { resolveConversationRoute } from '@/components/features/conversation/conversation-route';
import { resolveNotificationRoute } from '@/components/features/notifications/notification-route';
import { safeInvoke } from '@/utils/safe-invoke';

export const Route = createFileRoute('/_protected/t/$tenantId/go')({
  ssr: false,
  validateSearch: z.object({
    type: z.string().optional().catch(undefined),
    id: z.string().optional().catch(undefined),
    // Each ancestor arrives under its own id name. `parent` is the single-level predecessor,
    // still accepted because links already sent by email cannot be rewritten.
    workflow_id: z.string().optional().catch(undefined),
    template_id: z.string().optional().catch(undefined),
    execution_id: z.string().optional().catch(undefined),
    conversation_id: z.string().optional().catch(undefined),
    view_id: z.string().optional().catch(undefined),
    remedy_id: z.string().optional().catch(undefined),
    parent: z.string().optional().catch(undefined),
    tab: z.string().optional().catch(undefined),
    name: z.string().optional().catch(undefined),
  }),
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const { type, id, parent, tab, name, ...ancestorIds } = search;

    const conversationId = type === TLResourceType.MESSAGE ? ancestorIds.conversation_id || parent : undefined;
    if (conversationId) {
      const { data } = await safeInvoke(
        queryClient.ensureQueryData(conversationDetailsQueryOptions({ conversation_id: conversationId })),
      );
      throw redirect({
        ...resolveConversationRoute(
          {
            conversationId,
            conversationType: data?.conversation_type,
            workflowId: data?.metadata?.workflow_id,
            templateId: data?.metadata?.template_id,
          },
          params.tenantId,
        ),
        replace: true,
      });
    }

    const target = resolveNotificationRoute(
      {
        resourceType: type,
        resourceId: id,
        ancestorIds,
        parentResourceId: parent,
        navigationTab: tab,
        filename: name,
      },
      params.tenantId,
    );
    throw redirect(
      target
        ? { ...target, replace: true }
        : { to: '/t/$tenantId', params: { tenantId: params.tenantId }, replace: true },
    );
  },
  component: () => null,
});
